/**
 * 이상한 방 - 준비물 3택1(단계 안내 화면) · 클리어 확정 지급
 *
 * 근거 문서: 로드맵 확정안 4절 3번(로그라이크의 3택1 - **하나만** 둔다) · 3.2절(준비물은 넷뿐이다 · 가치 0인 봉투 없음) ·
 *           0절 판정표 18번(단서 카드 3택1은 기각, 준비물 3택1만 남는다) · `kits.ts` 머리말(사람 매치에는 자리가 없다)
 *
 * ## 고르는 것은 **가진 것 중에서**다
 *
 * 3택1이 "없는 것을 구경하는 화면"이 되면 그건 상점의 진열대이고, 진열대는 결제로 가는 문이다.
 * 그래서 제시되는 셋은 언제나 사물함에 실제로 있는 준비물이고, 하나도 없으면 **빈손**이다(빈 배열).
 * 빈손 화면은 잠긴 카드 셋이 아니라 "이번엔 챙길 것이 없다" 한 줄이어야 한다(화면 몫).
 *
 * ## 빈손이 이어지지 않게 - 매 클리어에 N 준비물 하나
 *
 * 봉투에서만 준비물이 나오면(R·SR 풀) 초반 플레이어는 3택1을 몇 판 동안 못 본다. 그래서 클리어마다
 * `COMMON_KIT_ID` 하나를 확정으로 준다. 고른 것이 **느린 시계**인 이유는 넷 중 가장 순한 값이기 때문이다 -
 * 수색 상한 +30초는 다 찾으면 조기 종료하므로 잘하는 사람에게는 아무 의미가 없고(`kits.ts`), 매 판 쌓여도
 * 곡선을 흔들지 않는다. 돋보기(외우기 +15초)를 매 판 주면 그건 확정 지급이 아니라 튜닝 변경이다.
 *
 * 지급은 새 산술을 쓰지 않고 `inventory.applyReward`를 그대로 부른다 - "산출물을 적용하는 자리는 여기 하나다"
 * (`inventory.ts` 머리말)를 지키려는 것이고, 그래야 준비물 보유 수의 증가가 봉투와 같은 한 줄에서만 일어난다.
 *
 * ## 결정론
 *
 * 제시되는 셋은 `createRng(`${accountSeed}:kit:${levelId}:${attempt}`)`에서 나온다. 계정마다 다르고,
 * 같은 단계·같은 시도에서는 **몇 번을 다시 열어도 같다** - 화면을 껐다 켜서 다른 셋을 받는 길이 있으면
 * 3택1이 아니라 무한 재추첨이 된다. `Math.random()`은 이 저장소 전체 금지다(`core/rng.ts`).
 */

import { createRng } from '../../core/rng'
import { applyReward } from './inventory'
import type { Inventory } from './inventory'
import type { Reward } from './envelopeTable'
import { KIT_IDS, type KitId } from './kits'

/* --- 상수 ------------------------------------------------------------------- */

/** 한 번에 제시하는 수. 넷 중 셋이라 "무엇을 포기하는가"가 언제나 하나 있다(로그라이크 3택1의 핵심). */
export const KIT_DRAFT_SIZE = 3

/** 클리어마다 확정으로 주는 준비물. 넷 중 가장 순한 값(머리말). */
export const COMMON_KIT_ID: KitId = 'slowClock'

/**
 * 그 확정 지급을 봉투 산출물의 모양으로 적은 것. 등급은 N이지만 준비물 산출물은 등급을 읽지 않는다
 * (`inventory.applyReward`의 `kit` 갈래 - 환급이 없으므로 등급도 탄창 보유 수도 쓰이지 않는다).
 */
export const CLEAR_KIT_REWARD: Reward = { kind: 'kit', kitId: COMMON_KIT_ID, count: 1 }

/* --- 시드 -------------------------------------------------------------------- */

/**
 * 제시 셋의 시드. 계정 시드 · 단계 · 시도 셋이 전부 들어간다 -
 * 계정이 빠지면 모두가 같은 셋을 보고, 시도가 빠지면 재도전에서 같은 셋이 굳는다.
 */
export function kitDraftSeed(accountSeed: string, levelId: number, attempt: number): string {
  return `${accountSeed}:kit:${String(levelId)}:${String(attempt)}`
}

/* --- 제시 -------------------------------------------------------------------- */

/**
 * 이번 판에 챙길 수 있는 준비물 셋. 보유한 **종류**에서 고르고, 종류가 셋보다 적으면 그만큼만 나온다.
 * 하나도 없으면 빈 배열(빈손)이다.
 *
 * 같은 준비물을 두 칸에 놓지 않는 이유는 그 화면이 3택1이 아니라 1택1이 되기 때문이다 - 돋보기를 다섯 개
 * 가진 사람에게 돋보기 셋을 보여 주면 선택이 사라진다. 그래서 개수는 화면이 옆에 적을 값이지 제시의 재료가 아니다.
 */
export function draftKits(
  inventory: Inventory,
  accountSeed: string,
  levelId: number,
  attempt: number,
): readonly KitId[] {
  const owned = KIT_IDS.filter((id) => inventory.kits[id] > 0)
  if (owned.length === 0) return []

  const rng = createRng(kitDraftSeed(accountSeed, levelId, attempt))
  return rng.shuffle(owned).slice(0, KIT_DRAFT_SIZE)
}

/* --- 지급 -------------------------------------------------------------------- */

/**
 * 클리어 한 번의 확정 지급. 새 사물함을 돌려주고, 값의 규칙은 `applyReward` 하나가 갖는다.
 *
 * 탄창 보유 수로 0을 넘기는 것은 준비물 갈래가 그 인자를 읽지 않기 때문이다(상한도 환급도 탄창의 것이다).
 * 그 사실이 바뀌면 이 호출이 먼저 틀리므로 `kitDraft.test.ts`가 지급 결과를 직접 확인한다.
 */
export function grantClearKit(inventory: Inventory): Inventory {
  return applyReward(inventory, CLEAR_KIT_REWARD, 'common', 0).inventory
}
